'use client';
import { useRef, useState } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

interface Column {
    content: string;
}

interface Slide {
    title: string;
    subtitle: string;
    backgroundVideo: string;
    overlayImage?: string;
    columns: Column[];
}

interface ProcessStepsSliderProps {
    slides?: Slide[];
}

export function ProcessStepsSlider({ 
    slides = []
}: ProcessStepsSliderProps) {
    const [currentSlide, setCurrentSlide] = useState(0);
    const sliderRef = useRef<Slider>(null);
    const [isPlaying, setIsPlaying] = useState(true);

    const settings = {
        infinite: false,
        speed: 500,
        autoplay: false,
        arrows: false,
        swipe: false,
        draggable: false,
        beforeChange: (_: number, next: number) => {
            setCurrentSlide(next);
        },
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            sliderRef.current?.slickGoTo(index);
        }
    };

    return (
        <div 
            className="relative w-full px-0 md:px-6"
            role="region"
            aria-label="Process Steps Slider"
        >
            <div className="h-[4rem] md:h-auto mb-4">
                <h2 className="text-3xl font-bold text-center drop-shadow-lg" id="slide-title">
                    {slides[currentSlide]?.title}
                </h2>
            </div>
            
            <div 
                className="flex justify-center gap-4 mb-4"
                role="tablist"
                aria-label="Process steps"
            >
                {slides.map((slide, index) => (
                    <button
                        key={index}
                        onClick={() => sliderRef.current?.slickGoTo(index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        role="tab"
                        aria-selected={currentSlide === index}
                        aria-controls={`slide-${index}`}
                        tabIndex={currentSlide === index ? 0 : -1}
                        className={`w-fit min-w-24 transition-all flex items-center justify-center flex-nowrap rounded-lg px-2 py-1
                            ${currentSlide === index
                                ? 'bg-[#008eaa] border-2 border-[#008eaa] min-h-10 rounded-full text-white focus:outline focus:outline-2 focus:outline-blue-600 drop-shadow-lg'
                                : 'bg-[#f1f1f2] text-[#222] border border-[#e0e0e0] hover:bg-[#e0e0e0] focus:outline focus:outline-2 focus:outline-blue-600'}
                        `}
                    >
                        {slide.subtitle}
                    </button>
                ))}
            </div>
            
            <div 
                className="slick-container overflow-hidden"
                role="tabpanel"
                id={`slide-${currentSlide}`}
                aria-labelledby="slide-title"
            >
                <Slider ref={sliderRef} {...settings}>
                    {slides.map((slide, slideIndex) => (
                        <div key={slideIndex}>
                            <div className="md:h-[380px] xl:h-[400px] rounded-2xl relative">
                                <video
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    className="w-full h-full md:object-cover rounded-2xl"
                                    aria-label={`Video for ${slide.title}`}
                                >
                                    <source src={slide.backgroundVideo} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                                {slide.overlayImage && (
                                    <div className="absolute inset-x-0 bottom-[45px] w-full transform translate-y-1/2 flex justify-end">
                                        <div className="w-[150px] md:w-[300px] lg:w-[300px] flex items-end justify-center">
                                            <img 
                                                src={slide.overlayImage} 
                                                alt={`Illustration for ${slide.title}`}
                                                className="md:w-[75%] object-contain drop-shadow-[0_8px_10px_rgba(0,0,0,0.25)]"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="mt-8 md:mt-16">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-4 rounded-lg md:block">
                                    <div 
                                        className="flex overflow-x-auto gap-6 md:grid md:grid-cols-3 pb-4 md:pb-0"
                                        role="list"
                                        aria-label={`Features for ${slide.title}`}
                                    >
                                        {slide.columns.map((column, columnIndex) => (
                                            <div 
                                                key={columnIndex} 
                                                className="bg-[rgb(241,241,242)] rounded-lg p-4 shadow-sm min-h-6 flex items-center [&_svg]:min-w-10 [&_svg]:min-h-10 [&_svg]:w-10 [&_svg]:h-10 min-w-[280px] md:min-w-0"
                                                role="listitem"
                                            >
                                                <div 
                                                    className='min-h-20 flex gap-4 items-center' 
                                                    dangerouslySetInnerHTML={{ __html: column.content }}
                                                    aria-label={`Feature ${columnIndex + 1} for ${slide.title}`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </Slider>
            </div>
        </div>
    );
}